import { useEffect, useMemo, useState } from 'react';
import { Pressable, ScrollView, Text, TextInput, View } from 'react-native';
import {
  Banknote,
  CreditCard,
  HandCoins,
  Minus,
  Plus,
  QrCode,
  Search,
  ShoppingBag,
  Trash2,
  Wallet,
} from 'lucide-react-native';

import { Button, Sheet } from '@/components/ui';
import { useFirestoreCollection } from '@/hooks/useFirestoreCollection';
import {
  crearVentasPetshop,
  productosPetshop,
  type ItemCarritoVenta,
} from '@/services/firebase/petshops';
import { useAuth } from '@/stores/authStore';
import { colors, radius, typography } from '@/theme/tokens';
import { formatBs } from '@/utils/format';
import { type MetodoPago, type PetshopId } from '@/types/domain';

interface Props {
  visible: boolean;
  onClose: () => void;
  petshop: PetshopId;
  petshopNombre?: string;
}

const METODOS: { id: MetodoPago; label: string; icon: typeof Banknote }[] = [
  { id: 'efectivo', label: 'Efectivo', icon: Banknote },
  { id: 'qr', label: 'QR', icon: QrCode },
  { id: 'transferencia', label: 'Transferencia', icon: HandCoins },
  { id: 'tarjeta', label: 'Tarjeta', icon: CreditCard },
  { id: 'otro', label: 'Otro', icon: Wallet },
];

export function NuevaVentaSheet({ visible, onClose, petshop, petshopNombre }: Props) {
  const perfil = useAuth((s) => s.perfil);
  const productosQ = useMemo(() => productosPetshop(petshop), [petshop]);
  const { data: productos } = useFirestoreCollection(productosQ);

  const [carrito, setCarrito] = useState<ItemCarritoVenta[]>([]);
  const [metodo, setMetodo] = useState<MetodoPago>('efectivo');
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) return;
    setCarrito([]);
    setMetodo('efectivo');
    setSearch('');
    setError(null);
    setSaving(false);
  }, [visible]);

  const productosFiltrados = useMemo(() => {
    const q = search.trim().toLowerCase();
    const arr = productos
      .slice()
      .sort((a, b) => a.categoria.localeCompare(b.categoria) || a.nombre.localeCompare(b.nombre));
    if (!q) return arr;
    return arr.filter(
      (p) =>
        p.nombre.toLowerCase().includes(q) ||
        p.categoria.toLowerCase().includes(q),
    );
  }, [productos, search]);

  const total = useMemo(
    () => carrito.reduce((sum, it) => sum + it.precio * it.cantidad, 0),
    [carrito],
  );

  const cantidadTotal = useMemo(
    () => carrito.reduce((sum, it) => sum + it.cantidad, 0),
    [carrito],
  );

  const agregarAlCarrito = (productoId: string) => {
    const p = productos.find((x) => x.id === productoId);
    if (!p) return;
    const stock = p.stock ?? 0;
    const enCarrito = carrito.find((it) => it.productoId === productoId)?.cantidad ?? 0;
    if (enCarrito >= stock) {
      setError(stock <= 0 ? `${p.nombre} no tiene stock.` : `Solo quedan ${stock} de ${p.nombre}.`);
      return;
    }
    setError(null);
    setCarrito((prev) => {
      const existe = prev.find((it) => it.productoId === productoId);
      if (existe) {
        return prev.map((it) =>
          it.productoId === productoId ? { ...it, cantidad: it.cantidad + 1 } : it,
        );
      }
      return [
        ...prev,
        {
          productoId: p.id,
          nombre: p.nombre,
          categoria: p.categoria,
          precio: p.precio,
          cantidad: 1,
        },
      ];
    });
  };

  const cambiarCantidad = (productoId: string, delta: number) => {
    if (delta > 0) {
      const stock = productos.find((p) => p.id === productoId)?.stock ?? 0;
      const actual = carrito.find((it) => it.productoId === productoId)?.cantidad ?? 0;
      if (actual >= stock) {
        setError(`Solo quedan ${stock} en stock.`);
        return;
      }
      setError(null);
    }
    setCarrito((prev) =>
      prev
        .map((it) =>
          it.productoId === productoId ? { ...it, cantidad: it.cantidad + delta } : it,
        )
        .filter((it) => it.cantidad > 0),
    );
  };

  const quitar = (productoId: string) => {
    setCarrito((prev) => prev.filter((it) => it.productoId !== productoId));
  };

  const onSave = async () => {
    setError(null);
    if (!perfil) return setError('Sesión inválida.');
    if (carrito.length === 0) return setError('Agregá al menos un producto al carrito.');

    setSaving(true);
    try {
      await crearVentasPetshop({
        petshop,
        items: carrito,
        metodoPago: metodo,
        fecha: new Date(),
        autorUid: perfil.uid,
        autorNombre: perfil.nombre,
      });
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo guardar.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet
      visible={visible}
      onClose={onClose}
      size="lg"
      title="Nueva venta"
      subtitle={petshopNombre ?? ''}
      footer={
        <>
          <Button label="Cancelar" variant="ghost" onPress={onClose} />
          <Button
            label={
              carrito.length === 0
                ? 'Agregá productos'
                : `Cobrar · ${formatBs(total)}`
            }
            iconLeft={<ShoppingBag size={16} color={colors.white} />}
            onPress={onSave}
            loading={saving}
            disabled={carrito.length === 0}
            variant="success"
          />
        </>
      }
    >
      {productos.length === 0 ? (
        <View style={{ padding: 14, borderRadius: radius.xl, backgroundColor: colors.warning[50] }}>
          <Text style={{ color: colors.warning[600], fontSize: 13, fontWeight: typography.weights.semibold }}>
            Aún no hay productos en el catálogo. Cargá productos desde la pestaña «Catálogo» antes de registrar ventas.
          </Text>
        </View>
      ) : (
        <>
          {/* Carrito */}
          {carrito.length > 0 ? (
            <View style={{ gap: 8 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <Text style={{ fontSize: 13, fontWeight: typography.weights.bold, color: colors.ink[800], letterSpacing: 0.4, textTransform: 'uppercase' }}>
                  Carrito · {cantidadTotal} {cantidadTotal === 1 ? 'unidad' : 'unidades'}
                </Text>
                <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 20, fontWeight: typography.weights.bold, color: colors.success[600] }}>
                  {formatBs(total)}
                </Text>
              </View>
              <View
                style={{
                  borderRadius: radius['2xl'],
                  borderWidth: 1,
                  borderColor: colors.success[500],
                  backgroundColor: colors.success[50],
                  overflow: 'hidden',
                }}
              >
                {carrito.map((it, idx) => (
                  <View
                    key={it.productoId ?? `item-${idx}`}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 10,
                      padding: 10,
                      borderBottomWidth: idx === carrito.length - 1 ? 0 : 1,
                      borderBottomColor: colors.ink[100],
                      backgroundColor: colors.white,
                    }}
                  >
                    <View style={{ flex: 1 }}>
                      <Text style={{ fontSize: 13, fontWeight: typography.weights.bold, color: colors.ink[900] }} numberOfLines={1}>
                        {it.nombre}
                      </Text>
                      <Text style={{ fontSize: 11, color: colors.ink[500] }}>
                        {formatBs(it.precio)} c/u
                      </Text>
                    </View>

                    {/* Stepper */}
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                      <Pressable
                        onPress={() => cambiarCantidad(it.productoId!, -1)}
                        style={({ hovered }) => ({
                          width: 28,
                          height: 28,
                          borderRadius: 8,
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: hovered ? colors.crema[100] : colors.crema[50],
                          borderWidth: 1,
                          borderColor: colors.ink[200],
                          // @ts-ignore web only
                          cursor: 'pointer',
                        })}
                      >
                        <Minus size={14} color={colors.ink[700]} />
                      </Pressable>
                      <Text style={{ width: 28, textAlign: 'center', fontSize: 14, fontWeight: typography.weights.bold, color: colors.ink[900] }}>
                        {it.cantidad}
                      </Text>
                      <Pressable
                        onPress={() => cambiarCantidad(it.productoId!, +1)}
                        style={({ hovered }) => ({
                          width: 28,
                          height: 28,
                          borderRadius: 8,
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: hovered ? colors.magenta[200] : colors.magenta[100],
                          // @ts-ignore web only
                          cursor: 'pointer',
                        })}
                      >
                        <Plus size={14} color={colors.magenta[700]} />
                      </Pressable>
                    </View>

                    <Text
                      style={{
                        width: 70,
                        textAlign: 'right',
                        fontFamily: typography.display.fontFamily,
                        fontSize: 14,
                        fontWeight: typography.weights.bold,
                        color: colors.success[600],
                      }}
                    >
                      {formatBs(it.precio * it.cantidad)}
                    </Text>

                    <Pressable
                      onPress={() => quitar(it.productoId!)}
                      hitSlop={6}
                      style={({ hovered }) => ({
                        width: 26,
                        height: 26,
                        borderRadius: 7,
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: hovered ? colors.danger[50] : 'transparent',
                        // @ts-ignore web only
                        cursor: 'pointer',
                      })}
                    >
                      <Trash2 size={13} color={colors.danger[600]} />
                    </Pressable>
                  </View>
                ))}
              </View>
            </View>
          ) : null}

          {/* Buscador */}
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              backgroundColor: colors.crema[50],
              borderRadius: radius.full,
              paddingHorizontal: 14,
              height: 44,
              borderWidth: 1,
              borderColor: colors.ink[100],
            }}
          >
            <Search size={16} color={colors.ink[400]} />
            <TextInput
              placeholder="Buscar producto…"
              placeholderTextColor={colors.ink[400]}
              value={search}
              onChangeText={setSearch}
              style={{
                flex: 1,
                fontSize: 14,
                color: colors.ink[900],
                fontFamily: typography.body.fontFamily,
                // @ts-ignore web only
                outlineStyle: 'none',
              }}
            />
          </View>

          {/* Lista de productos: tocar agrega 1 al carrito */}
          <View
            style={{
              maxHeight: 260,
              borderRadius: radius['2xl'],
              borderWidth: 1,
              borderColor: colors.ink[100],
              backgroundColor: colors.white,
              overflow: 'hidden',
            }}
          >
            <ScrollView style={{ maxHeight: 260 }}>
              {productosFiltrados.length === 0 ? (
                <View style={{ padding: 18, alignItems: 'center' }}>
                  <Text style={{ color: colors.ink[500], fontSize: 13 }}>
                    Sin productos para «{search}».
                  </Text>
                </View>
              ) : (
                productosFiltrados.map((p, idx) => {
                  const enCarrito = carrito.find((it) => it.productoId === p.id);
                  const stock = p.stock ?? 0;
                  const disponible = stock - (enCarrito?.cantidad ?? 0);
                  const sinStock = disponible <= 0;
                  return (
                    <Pressable
                      key={p.id}
                      onPress={sinStock ? undefined : () => agregarAlCarrito(p.id)}
                      style={({ hovered }) => ({
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 12,
                        paddingHorizontal: 14,
                        paddingVertical: 10,
                        backgroundColor: hovered && !sinStock ? colors.crema[50] : 'transparent',
                        borderBottomWidth: idx === productosFiltrados.length - 1 ? 0 : 1,
                        borderBottomColor: colors.ink[100],
                        opacity: sinStock ? 0.55 : 1,
                        // @ts-ignore web only
                        cursor: sinStock ? 'not-allowed' : 'pointer',
                      })}
                    >
                      <View style={{ flex: 1 }}>
                        <Text
                          style={{ fontSize: 14, fontWeight: typography.weights.bold, color: colors.ink[900] }}
                          numberOfLines={1}
                        >
                          {p.nombre}
                        </Text>
                        <Text style={{ fontSize: 11, color: stock <= 0 ? colors.danger[600] : colors.ink[500], marginTop: 2 }}>
                          {p.categoria} · {stock <= 0 ? 'sin stock' : `stock ${stock}`}
                        </Text>
                      </View>
                      <Text
                        style={{
                          fontFamily: typography.display.fontFamily,
                          fontSize: 15,
                          fontWeight: typography.weights.bold,
                          color: colors.ink[800],
                        }}
                      >
                        {formatBs(p.precio)}
                      </Text>
                      <View
                        style={{
                          width: 36,
                          height: 32,
                          borderRadius: 10,
                          backgroundColor: sinStock ? colors.ink[100] : enCarrito ? colors.success[500] : colors.magenta[100],
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexDirection: 'row',
                          gap: 2,
                        }}
                      >
                        <Plus size={14} color={sinStock ? colors.ink[400] : enCarrito ? colors.white : colors.magenta[700]} />
                        {enCarrito ? (
                          <Text style={{ color: sinStock ? colors.ink[500] : colors.white, fontWeight: typography.weights.bold, fontSize: 11 }}>
                            {enCarrito.cantidad}
                          </Text>
                        ) : null}
                      </View>
                    </Pressable>
                  );
                })
              )}
            </ScrollView>
          </View>

          {/* Método de pago como chips */}
          <View style={{ gap: 8 }}>
            <Text style={{ fontSize: 13, fontWeight: typography.weights.semibold, color: colors.ink[700] }}>
              Método de pago
            </Text>
            <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
              {METODOS.map((m) => {
                const Icon = m.icon;
                const active = metodo === m.id;
                return (
                  <Pressable
                    key={m.id}
                    onPress={() => setMetodo(m.id)}
                    style={({ hovered }) => ({
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 6,
                      paddingHorizontal: 14,
                      height: 38,
                      borderRadius: radius.full,
                      backgroundColor: active ? colors.magenta[700] : hovered ? colors.crema[100] : colors.crema[50],
                      borderWidth: 1.5,
                      borderColor: active ? colors.magenta[700] : colors.ink[200],
                      // @ts-ignore web only
                      cursor: 'pointer',
                    })}
                  >
                    <Icon size={14} color={active ? colors.white : colors.ink[600]} />
                    <Text
                      style={{
                        color: active ? colors.white : colors.ink[800],
                        fontWeight: typography.weights.bold,
                        fontSize: 12,
                      }}
                    >
                      {m.label}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>
        </>
      )}

      {error ? (
        <View style={{ padding: 12, borderRadius: 12, backgroundColor: colors.danger[50] }}>
          <Text style={{ color: colors.danger[600], fontSize: 13, fontWeight: typography.weights.semibold }}>
            {error}
          </Text>
        </View>
      ) : null}
    </Sheet>
  );
}
